import React, { ComponentProps, useEffect, useRef } from "react";

import {
  getContactLink,
  goTo,
  useAppType,
  useFeatureFlag,
  useGeneralContext,
  useGetFarms,
  useLocation,
  useNavContext,
  useTranslation,
  useUserContext,
} from "utils";

import { FEATURE_FLAGS } from "consts";

import { User } from "models";

import { Icon, Layout, Link, Menu, Svg, Tag, Tooltip } from "components";

import { RailNavItem, buildRailNavGroups, getRailNavActiveKey } from "pages/app/_app/components/rail-nav/railNavItems";

const { Sider } = Layout;

const EXPANDED_WIDTH = 256;
const COLLAPSED_WIDTH = 96;

enum SUPPORT_KEYS {
  SUPPORT = "SUPPORT",
  FARMER_TEAM = "FARMER_TEAM",
  CONTACT_US = "CONTACT_US",
}

type SupportMenuItems = ComponentProps<typeof Menu>["items"];

const renderItem = (item: RailNavItem, activeKey: string | null, collapseOnSmallScreens: () => void, collapsed: boolean) => {
  const active = item.key === activeKey;
  const link = (
    <Link
      key={item.key}
      to={item.to}
      state={item.state}
      tracking={false}
      className={`rail-nav-item${active ? " active" : ""}`}
      dataTestId={item.dataTestId}
      aria-current={active ? "page" : undefined}
      onClick={collapseOnSmallScreens}
    >
      <span className="rail-nav-item-icon">
        <Icon iconName={item.iconName} />
        {collapsed && item.tag ? <span className="rail-nav-item-dot" data-testid={`${item.dataTestId}-dot`} aria-hidden /> : null}
      </span>
      <span className="rail-nav-item-label">{item.label}</span>
      {!collapsed && item.tag ? <Tag className="rail-nav-item-tag" colour="blue" text={item.tag} /> : null}
    </Link>
  );

  if (!collapsed) return link;

  return (
    <Tooltip key={item.key} variant="compact" placement="right" trigger={["hover", "focus"]} title={item.tag ? `${item.label} · ${item.tag}` : item.label}>
      {link}
    </Tooltip>
  );
};

/** Grouped side navigation; collapsed it is a 96px rail that keeps a label under every icon. */
export const RailNav = () => {
  const { largeDesktop } = useAppType();
  const { railNavCollapsed: collapsed, setRailNavCollapsed } = useGeneralContext();
  const { showNavbar }: { showNavbar: boolean } = useNavContext();
  const location = useLocation();
  const { t } = useTranslation("nav");
  const { user } = useUserContext();
  const { currentFarm, isPending: isFarmPending } = useGetFarms();
  const { isEnabled: isCopilotEnabled } = useFeatureFlag(FEATURE_FLAGS.COPILOT);
  const { isEnabled: isSoilSightEnabled } = useFeatureFlag(FEATURE_FLAGS.SOIL_SIGHT_INTEGRATION);
  const siderRef = useRef<HTMLDivElement>(null);

  // below the large-desktop width the expanded rail overlays the page, so clicking outside closes it
  useEffect(() => {
    if (largeDesktop || collapsed) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (siderRef.current && !siderRef.current.contains(e.target as Node)) {
        setRailNavCollapsed(true);
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, [collapsed, largeDesktop, setRailNavCollapsed]);

  if (!user || !showNavbar) return null;

  const { farm } = user as unknown as User;
  const groups = buildRailNavGroups({
    farmOnboarded: !!farm?.farmOnboarded,
    farmResolved: !isFarmPending,
    carbonProgram: currentFarm?.carbonProgram ?? undefined,
    isSoilSightEnabled,
    isCopilotEnabled,
    t,
  });
  const activeKey = getRailNavActiveKey(location.pathname);

  const collapseOnSmallScreens = () => {
    if (!largeDesktop) setRailNavCollapsed(true);
  };

  // both entries open the contact page
  const handleSupportClick = () => {
    collapseOnSmallScreens();
    goTo(undefined, null, getContactLink(), true);
  };

  const supportMenuItems: SupportMenuItems = [
    {
      key: SUPPORT_KEYS.SUPPORT,
      icon: <Icon iconName="tabler/info-circle" dataTestId="rail-nav-support-icon" />,
      label: t("railNav.support.title"),
      // the flyout is portaled to <body>, so it carries its own class for the rail's styles
      popupClassName: "rail-nav-support-popup",
      children: [
        { key: SUPPORT_KEYS.FARMER_TEAM, icon: <Icon iconName="tabler/users" />, label: t("railNav.support.farmerTeam") },
        { key: SUPPORT_KEYS.CONTACT_US, icon: <Icon iconName="klim/envelope" />, label: t("railNav.support.contactUs") },
      ],
    },
  ];

  return (
    <div data-testid="rail-nav" ref={siderRef}>
      <Sider
        className={`rail-nav${collapsed ? " collapsed" : ""}`}
        collapsed={collapsed}
        width={EXPANDED_WIDTH}
        collapsedWidth={COLLAPSED_WIDTH}
        trigger={null}
      >
        <div className="rail-nav-brandmark">
          {/* both marks are always mounted and toggled with CSS, so expanding never waits for a lazy-loaded svg */}
          <Link className="rail-nav-brandmark-link" to="/dashboard" tracking={false} dataTestId="rail-nav-brandmark" aria-label={t("railNav.brandmark")}>
            <Svg className="rail-nav-brandmark-small" svgName="icons/klim/brandmark" width={24} height={24} />
            <Svg className="rail-nav-brandmark-full" svgName="icons/klim/brandmark-with-text" width={84} height={24} />
          </Link>
          <button
            type="button"
            className="rail-nav-toggle"
            data-testid="rail-nav-toggle"
            aria-label={collapsed ? t("railNav.expandMenu") : t("railNav.collapseMenu")}
            aria-expanded={!collapsed}
            onClick={() => setRailNavCollapsed(!collapsed)}
          >
            <Icon className="rail-nav-toggle-icon" iconName="tabler/chevrons-left" />
            {collapsed ? <span className="rail-nav-toggle-label">{t("railNav.menu")}</span> : null}
          </button>
        </div>

        <nav className="rail-nav-groups" aria-label={t("railNav.menu")} data-testid="rail-nav-groups">
          {groups.map((group) => (
            // the heading is a plain span (and a divider when collapsed), so the group carries its own name
            <div key={group.key} className="rail-nav-group" role={group.heading ? "group" : undefined} aria-label={group.heading}>
              {group.heading ? collapsed ? <div className="rail-nav-divider" aria-hidden /> : <span className="rail-nav-heading">{group.heading}</span> : null}
              {group.items.map((item) => renderItem(item, activeKey, collapseOnSmallScreens, collapsed))}
            </div>
          ))}
        </nav>

        <Menu
          className="rail-nav-support"
          mode="vertical"
          items={supportMenuItems}
          selectedKeys={[]}
          expandIcon={<Icon iconName="tabler/chevron-right" size="s" dataTestId="rail-nav-support-chevron" />}
          onClick={handleSupportClick}
        />
      </Sider>
    </div>
  );
};
